// Permanent left rail. Four sections (Songs, Vault, Mine, Community)
// stacked as a strict accordion — only one body is open at a time,
// the other section headers still show their label + count. On
// mobile (< md) the rail becomes an off-canvas drawer triggered
// from TransportDeck.
//
// Desktop also supports rail-level collapse to a 44px "spine":
// vertical strips with a phosphor LED + rotated eyebrow label per
// section. Clicking a strip expands the rail and switches to that
// section. Collapse and section state both live in App.tsx so the
// parent ResizableSideRail can override its width to the spine size
// and TransportDeck's ⚒ Forge button can switch sections imperatively.
//
// MINE has a second "forge" mode that re-homes the dissolved
// ForgePanel: when forgeActive is true, MINE's body shows the
// authoring pipeline (verb tabs → buffer/trim → commit) instead of
// the sample list. The rail-level collapse button is hidden while
// forge mode is active so an accidental collapse can't unmount the
// in-flight buffer.

import { memo, useEffect, useRef } from 'react';
import { SectionCard } from '../blocks/library/SectionCard';
import { SongList } from './SongList';
import {
  SoundLibrary,
  type PreviewOps,
} from '../blocks/library/SoundLibrary';
import { MineTabs } from '../blocks/library/MineTabs';
import { MineForge } from '../blocks/library/MineForge';
import { CommunityTabs } from '../blocks/library/CommunityTabs';
import { useSampleStore } from '../blocks/library/SampleStoreContext';
import { SOUND_CATALOG } from '../blocks/library/catalog';
import type { Song } from '../types';
import type { StatusLevel } from '../strudel';

export type RackSectionId = 'songs' | 'vault' | 'mine' | 'community';

type Props = {
  songs: Song[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleFavourite: (id: string) => void;
  onNew: () => void;
  onExport: () => void;
  onImportFile: (file: File) => void;
  onOpenSettings: () => void;
  /** Engages forge mode in the MINE section, expanding the rail and
   *  switching the active section as needed. App.tsx owns the
   *  transition; the rail just calls this. */
  onOpenForge: () => void;
  preview: PreviewOps;
  /** Mobile-only: drives the overlay slide state. Ignored at md+. */
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** Desktop-only: when true, the rail renders the spine UI. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Which section is currently expanded. Controlled by App.tsx so
   *  imperative section switches (e.g. ⚒ Forge from TransportDeck)
   *  stay coordinated with rail-level state. */
  expandedSection: RackSectionId;
  onSwitchSection: (id: RackSectionId) => void;
  /** When true, the MINE section's body shows MineForge instead of
   *  the sample list. */
  forgeActive: boolean;
  onForgeBack: () => void;
  /** Threaded into MineForge's commit/save status reporting. */
  onStatus: (message: string, level?: StatusLevel) => void;
  /** Open the Publish-song modal for the active song. Disabled when
   *  there's no active song. */
  onPublishActive: () => void;
  /** True iff there's an active song to publish. */
  canPublish: boolean;
  /** Called when a community song was forked — caller seeds a new
   *  local Song from the forked code. */
  onForked: (params: { title: string; code: string; cloudPatchId: string }) => void;
  /** Called when the user clicks Play on a community song. */
  onPlayPatch: (params: { title: string; code: string; cloudPatchId: string }) => void;
  /** Open the Learn modal — wired to the book glyph in the rail top bar. */
  onOpenLearn: () => void;
  /** Briefly pulse the Songs section's LED — used by Learn to give
   *  "eject" feedback when a snippet is sent into the library. */
  songsPulse: boolean;
};

// Memoized: the parent re-renders at the hap-poll rate (12fps during
// playback) because useHapData lives there. None of the rack's props
// depend on frame data, so memoization skips reconciling the 40+
// SoundLibrary cards on every tick.
export const LeftRack = memo(LeftRackInner);

function LeftRackInner({
  songs,
  activeId,
  onSelect,
  onDelete,
  onToggleFavourite,
  onNew,
  onExport,
  onImportFile,
  onOpenSettings,
  onOpenForge,
  preview,
  mobileOpen,
  onMobileClose,
  collapsed,
  onToggleCollapsed,
  expandedSection,
  onSwitchSection,
  forgeActive,
  onForgeBack,
  onStatus,
  onPublishActive,
  canPublish,
  onForked,
  onPlayPatch,
  onOpenLearn,
  songsPulse,
}: Props) {
  const { samples: mineSamples, loading: mineLoading } = useSampleStore();

  // Dismiss mobile overlay with Escape.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, onMobileClose]);

  const switchSection = (id: RackSectionId) => {
    if (id === expandedSection) return;
    onSwitchSection(id);
  };

  // Spine click: expand the rail AND switch to that section.
  const expandToSection = (id: RackSectionId) => {
    if (id !== expandedSection) onSwitchSection(id);
    if (collapsed) onToggleCollapsed();
  };

  return (
    <>
      {/* Mobile scrim. Clicking dismisses the overlay. */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-zinc-950/70 backdrop-blur-sm"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        aria-label="Rack"
        className={
          'bg-zinc-900 border-r border-zinc-900 flex flex-col overflow-hidden ' +
          // At md+ the rail's outer width is owned by ResizableSideRail
          // — fill its column. On mobile the off-canvas drawer takes its
          // height from `fixed inset-y-0`; at md+ that source is gone, so
          // `md:h-full md:min-h-0` re-anchors the aside to the wrapper's
          // height. Without it the aside falls back to content height and
          // the accordion's flex-1 / overflow-y-auto chain has nothing to
          // fill or scroll within (matches ChatPanel's root sizing).
          'md:relative md:w-full md:h-full md:min-h-0 md:translate-x-0 md:z-auto md:shadow-none md:flex ' +
          'fixed inset-y-0 left-0 z-50 w-[300px] max-w-[85vw] transition-transform duration-200 ease-out shadow-2xl ' +
          // Mobile-only safe-area insets — drawer pinned to the left
          // viewport edge in standalone PWA mode would otherwise put
          // its top content under the iPhone Dynamic Island and its
          // book-icon column under any landscape notch. Reset on md+
          // where the rail is a static column inside ResizableSideRail.
          'safe-pt safe-pl md:!pt-0 md:!pl-0 ' +
          (mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')
        }
      >
        {/* Spine UI is desktop-only. On mobile (drawer mode) we always
            show the full layout, so collapsed is ignored under md. */}
        {collapsed ? (
          <SpineLayout
            songsCount={songs.length}
            vaultCount={SOUND_CATALOG.length}
            mineCount={mineLoading ? '···' : mineSamples.length}
            activeSection={expandedSection}
            forgeActive={forgeActive}
            onExpandToSection={expandToSection}
            onUncollapse={onToggleCollapsed}
          />
        ) : (
          <FullLayout
            songs={songs}
            activeId={activeId}
            onSelect={onSelect}
            onDelete={onDelete}
            onToggleFavourite={onToggleFavourite}
            onNew={onNew}
            onExport={onExport}
            onImportFile={onImportFile}
            onOpenSettings={onOpenSettings}
            onOpenForge={onOpenForge}
            preview={preview}
            onMobileClose={onMobileClose}
            expanded={expandedSection}
            onSwitchSection={switchSection}
            onCollapseRail={onToggleCollapsed}
            mineCount={mineLoading ? '···' : mineSamples.length}
            forgeActive={forgeActive}
            onForgeBack={onForgeBack}
            onStatus={onStatus}
            onPublishActive={onPublishActive}
            canPublish={canPublish}
            onForked={onForked}
            onPlayPatch={onPlayPatch}
            onOpenLearn={onOpenLearn}
            songsPulse={songsPulse}
          />
        )}
      </aside>
    </>
  );
}

// ─── Full layout (expanded) ─────────────────────────────────────────

function FullLayout({
  songs,
  activeId,
  onSelect,
  onDelete,
  onToggleFavourite,
  onNew,
  onExport,
  onImportFile,
  onOpenSettings,
  onOpenForge,
  preview,
  onMobileClose,
  expanded,
  onSwitchSection,
  onCollapseRail,
  mineCount,
  forgeActive,
  onForgeBack,
  onStatus,
  onPublishActive,
  canPublish,
  onForked,
  onPlayPatch,
  onOpenLearn,
  songsPulse,
}: {
  songs: Song[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleFavourite: (id: string) => void;
  onNew: () => void;
  onExport: () => void;
  onImportFile: (file: File) => void;
  onOpenSettings: () => void;
  onOpenForge: () => void;
  preview: PreviewOps;
  onMobileClose: () => void;
  expanded: RackSectionId;
  onSwitchSection: (id: RackSectionId) => void;
  onCollapseRail: () => void;
  mineCount: number | string;
  forgeActive: boolean;
  onForgeBack: () => void;
  onStatus: (message: string, level?: StatusLevel) => void;
  onPublishActive: () => void;
  canPublish: boolean;
  onForked: (params: { title: string; code: string; cloudPatchId: string }) => void;
  onPlayPatch: (params: { title: string; code: string; cloudPatchId: string }) => void;
  onOpenLearn: () => void;
  songsPulse: boolean;
}) {
  return (
    <>
      <RailTopBar
        onCollapseRail={onCollapseRail}
        // Hide the collapse affordance while a forge buffer is in
        // flight — collapsing would unmount MineForge and lose the
        // unsaved take. Discard or Save first to make it available.
        canCollapse={!forgeActive}
        onOpenLearn={onOpenLearn}
      />

      <SectionCard
        id="songs"
        label="Songs"
        count={songs.length}
        expanded={expanded === 'songs'}
        onToggle={() => onSwitchSection('songs')}
        pulse={songsPulse}
        action={{
          label: <GearIcon />,
          title: 'Settings / recovery',
          onClick: onOpenSettings,
        }}
      >
        <SongsBody
          songs={songs}
          activeId={activeId}
          onSelect={(id) => {
            onSelect(id);
            onMobileClose();
          }}
          onDelete={onDelete}
            onToggleFavourite={onToggleFavourite}
          onNew={onNew}
          onExport={onExport}
          onImportFile={onImportFile}
          onCloseMobile={onMobileClose}
          onPublishActive={onPublishActive}
          canPublish={canPublish}
        />
      </SectionCard>

      <SectionCard
        id="vault"
        label="Vault"
        count={SOUND_CATALOG.length}
        description={
          <>
            <span className="text-zinc-300">{SOUND_CATALOG.length}</span>{' '}
            pre-built voices · click to drop one in
          </>
        }
        expanded={expanded === 'vault'}
        onToggle={() => onSwitchSection('vault')}
      >
        <SoundLibrary preview={preview} />
      </SectionCard>

      <SectionCard
        id="mine"
        label={forgeActive ? 'Mine · Forge' : 'Mine'}
        count={forgeActive ? undefined : mineCount}
        accentDot="bg-emerald-300"
        accentInk="text-emerald-300"
        description={
          forgeActive
            ? undefined
            : <>your forged samples · click to drop one in</>
        }
        expanded={expanded === 'mine'}
        onToggle={() => onSwitchSection('mine')}
        action={
          forgeActive
            ? undefined
            : {
                label: <span aria-hidden="true">⚒</span>,
                title: 'Open the Sample Forge',
                onClick: onOpenForge,
              }
        }
      >
        {forgeActive ? (
          <MineForge onStatus={onStatus} onBack={onForgeBack} />
        ) : (
          <MineTabs preview={preview} onOpenForge={onOpenForge} onStatus={onStatus} />
        )}
      </SectionCard>

      <SectionCard
        id="community"
        label="Community"
        accentDot="bg-cyan-300"
        accentInk="text-cyan-300"
        description={<>shared by other harness users · click to import</>}
        expanded={expanded === 'community'}
        onToggle={() => onSwitchSection('community')}
      >
        <CommunityTabs
          onStatus={onStatus}
          onForked={onForked}
          onPlayPatch={onPlayPatch}
        />
      </SectionCard>
    </>
  );
}

// ─── Spine layout (collapsed) ──────────────────────────────────────

function SpineLayout({
  songsCount,
  vaultCount,
  mineCount,
  activeSection,
  forgeActive,
  onExpandToSection,
  onUncollapse,
}: {
  songsCount: number;
  vaultCount: number;
  mineCount: number | string;
  activeSection: RackSectionId;
  forgeActive: boolean;
  onExpandToSection: (id: RackSectionId) => void;
  onUncollapse: () => void;
}) {
  return (
    <div className="hidden md:flex flex-1 flex-col items-stretch min-h-0">
      <button
        type="button"
        onClick={onUncollapse}
        title="Expand library rail"
        aria-label="Expand library rail"
        className="h-7 shrink-0 grid place-items-center text-zinc-500 hover:text-yellow-300 border-b border-zinc-900 transition-colors"
      >
        <ChevronRight />
      </button>

      <SpineStrip
        label="Songs"
        count={songsCount}
        active={activeSection === 'songs'}
        onClick={() => onExpandToSection('songs')}
      />
      <SpineStrip
        label="Vault"
        count={vaultCount}
        active={activeSection === 'vault'}
        onClick={() => onExpandToSection('vault')}
      />
      <SpineStrip
        label={forgeActive ? 'Forge' : 'Mine'}
        count={forgeActive ? '⚒' : mineCount}
        active={activeSection === 'mine'}
        onClick={() => onExpandToSection('mine')}
        accent="emerald"
        pulse={forgeActive}
      />
      <SpineStrip
        label="Community"
        count="↗"
        active={activeSection === 'community'}
        onClick={() => onExpandToSection('community')}
        accent="cyan"
      />
    </div>
  );
}

function SpineStrip({
  label,
  count,
  active,
  onClick,
  accent = 'yellow',
  pulse = false,
}: {
  label: string;
  count: number | string;
  active: boolean;
  onClick: () => void;
  /** LED color when active. Maps to a DESIGN.md voice-LED token. */
  accent?: 'yellow' | 'emerald' | 'cyan';
  /** Pulse the LED to indicate unsaved work — used while forge mode
   *  has a buffer in progress and the rail is collapsed. */
  pulse?: boolean;
}) {
  const activeDot =
    accent === 'emerald'
      ? 'bg-emerald-300 shadow-[0_0_8px_rgba(110,231,183,0.6)]'
      : accent === 'cyan'
        ? 'bg-cyan-300 shadow-[0_0_8px_rgba(103,232,249,0.6)]'
        : 'bg-yellow-300 shadow-[0_0_8px_rgba(253,224,71,0.6)]';
  const activeInk =
    accent === 'emerald'
      ? 'text-emerald-300'
      : accent === 'cyan'
        ? 'text-cyan-300'
        : 'text-yellow-300';
  const activeBg =
    accent === 'emerald'
      ? 'bg-emerald-300/5 hover:bg-emerald-300/10'
      : accent === 'cyan'
        ? 'bg-cyan-300/5 hover:bg-cyan-300/10'
        : 'bg-yellow-300/5 hover:bg-yellow-300/10';

  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} (${count})`}
      aria-label={`Expand ${label}`}
      className={
        'group flex-1 min-h-0 flex flex-col items-center justify-between py-3 ' +
        'border-b border-zinc-900 transition-colors ' +
        (active ? activeBg : 'hover:bg-zinc-800/40')
      }
    >
      <span
        aria-hidden="true"
        className={
          'h-1.5 w-1.5 rounded-full ' +
          (active ? activeDot : 'bg-zinc-700 group-hover:bg-yellow-300/60') +
          (pulse ? ' animate-pulse' : '')
        }
      />
      <span
        // Vertical text. writing-mode reads bottom-to-top with the
        // baseline on the right edge — natural for narrow rails and
        // doesn't fight letter-spacing the way rotate() does.
        className={
          'font-display text-[10px] tracking-[0.38em] uppercase leading-none ' +
          '[writing-mode:vertical-rl] [text-orientation:mixed] rotate-180 ' +
          (active ? activeInk : 'text-zinc-500 group-hover:text-zinc-300')
        }
      >
        {label}
      </span>
      <span className="text-[9px] text-zinc-600 tabular-nums tracking-wider">
        {count}
      </span>
    </button>
  );
}

function RailTopBar({
  onCollapseRail,
  canCollapse,
  onOpenLearn,
}: {
  onCollapseRail: () => void;
  canCollapse: boolean;
  onOpenLearn: () => void;
}) {
  return (
    <div className="flex h-7 shrink-0 border-b border-zinc-900 px-1 items-center justify-between">
      <button
        type="button"
        onClick={onOpenLearn}
        title="Open Learn (?)"
        aria-label="Open Learn"
        className="text-zinc-500 hover:text-yellow-300 px-2 py-0.5 transition-colors"
      >
        <BookIcon />
      </button>
      {canCollapse && (
        <button
          type="button"
          onClick={onCollapseRail}
          title="Collapse library rail"
          aria-label="Collapse library rail"
          className="hidden md:block text-zinc-500 hover:text-yellow-300 px-2 py-0.5 transition-colors"
        >
          <ChevronLeft />
        </button>
      )}
    </div>
  );
}

// ─── Songs body ─────────────────────────────────────────────────────

function SongsBody({
  songs,
  activeId,
  onSelect,
  onDelete,
  onToggleFavourite,
  onNew,
  onExport,
  onImportFile,
  onCloseMobile,
  onPublishActive,
  canPublish,
}: {
  songs: Song[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleFavourite: (id: string) => void;
  onNew: () => void;
  onExport: () => void;
  onImportFile: (file: File) => void;
  onCloseMobile: () => void;
  onPublishActive: () => void;
  canPublish: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <div className="px-3 py-2 border-b border-zinc-900 flex gap-1.5 flex-wrap">
        <RackActionButton onClick={onNew} title="New song">
          + New
        </RackActionButton>
        <RackActionButton onClick={onExport} title="Export all songs as JSON">
          Export
        </RackActionButton>
        <RackActionButton
          onClick={() => fileInputRef.current?.click()}
          title="Import songs from JSON"
        >
          Import
        </RackActionButton>
        <RackActionButton
          onClick={onPublishActive}
          title={canPublish ? 'Publish current song to community' : 'Open a song first'}
          disabled={!canPublish}
        >
          Publish
        </RackActionButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImportFile(file);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={onCloseMobile}
          className="md:hidden text-zinc-500 hover:text-zinc-200 text-lg leading-none px-2 ml-auto"
          aria-label="Close rack"
        >
          ×
        </button>
      </div>

      <div role="list" className="flex-1 min-h-0 overflow-y-auto p-2 scrollbar-thin">
        <SongList
          songs={songs}
          activeId={activeId}
          onSelect={onSelect}
          onDelete={onDelete}
            onToggleFavourite={onToggleFavourite}
        />
      </div>
    </>
  );
}

function RackActionButton({
  onClick,
  title,
  children,
  disabled = false,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="flex-1 min-w-0 bg-zinc-800 text-zinc-100 border border-zinc-800 px-2 py-1.5 text-[11px] font-semibold rounded hover:bg-zinc-700 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

function GearIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="w-3.5 h-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="2.25" />
      <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4" />
    </svg>
  );
}

function ChevronLeft() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 3 L5 8 L10 13" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 2.5h4a2 2 0 0 1 2 2v9" />
      <path d="M13 2.5H9a2 2 0 0 0-2 2v9" />
      <path d="M3 2.5v9h4" />
      <path d="M13 2.5v9H9" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3 L11 8 L6 13" />
    </svg>
  );
}
